import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { supabase } from './lib/supabase';
import { tShare } from './lib/i18n';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Self-hosted: the map is a demo-critical screen and should not depend on
// a third-party CDN being reachable.
const GEO_URL = `${process.env.PUBLIC_URL}/world-110m.json`;

const C = {
  bg: '#07101a', card: '#0d1a27', border: '#1e3347',
  accent: '#009EC2', accentHi: '#4DD4F0', text: '#E6FBFF',
  sub: '#7ab8c8', muted: '#3d6b7a', gold: '#F4A928', green: '#27C97A',
  rose: '#f472b6',
};

// Light theme for TripView (printable)
const CL = {
  bg: '#ffffff', card: '#f7f9fa', border: '#e2e8ed',
  accent: '#007fa3', accentHi: '#006f8f', text: '#0f1f2e',
  sub: '#3a6070', muted: '#8fa8b4', gold: '#c47f00', green: '#1a9457',
  rose: '#c2366a',
};

const SLOT_TYPE_COLORS = { WE: '#4DD4F0', longWE: '#F4A928', short: '#27C97A', long: '#f472b6' };
const SLOT_TYPE_LABELS_FR = { WE: 'Week-end', longWE: 'Long WE', short: 'Court séjour', long: 'Long séjour' };

const GUIDE_ICONS = { highlights: '📍', hidden_gems: '🔮', bars: '🍸', restaurants: '🍽️' };

const POLA_ROTS  = [-3, 4, -2, 5, -4, 3, -5, 2, -3, 4];
const POLA_MTS   = [0, 14, -6, 18, 4, -10, 8, -4, 2, 16];

// ─── Root ────────────────────────────────────────────────────────────────────

export default function ShareView() {
  const { token } = useParams();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(false);

  // Load Caveat font once
  useEffect(() => {
    if (document.getElementById('caveat-font')) return;
    const link = document.createElement('link');
    link.id = 'caveat-font';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Caveat:wght@700&display=swap';
    document.head.appendChild(link);
  }, []);


  useEffect(() => {
    supabase.from('shared_topn').select('*').eq('token', token).single()
      .then(({ data: row }) => {
        if (!row) { setError(true); setLoading(false); return; }
        // Anonymous viewers no longer hold UPDATE on shared_topn; this RPC is
        // the only path that can touch view_count, and only that column.
        supabase.rpc('bump_shared_topn_view', { p_token: token });
        setData(row);
        setLoading(false);
        // Dynamic page title for print/PDF
        const p = row.payload || {};
        const mode = p.mode || 'list';
        const owner = p.ownerName || '';
        const dest = p.dest?.name || '';
        if (mode === 'trip' && dest) document.title = `Voyage à ${dest}${owner ? ` · ${owner}` : ''}`;
        else if (mode === 'topn' && owner) document.title = `Top ${p.planCount || 5} de ${owner} · Plotted`;
        else if (dest) document.title = `${dest} · Plotted`;
        else document.title = 'Plotted';
      });
  }, [token]);

  const goSignup = () => { window.location.href = window.location.origin; };

  if (loading) return (
    <div style={{ background: C.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 28, fontWeight: 900, color: C.text, letterSpacing: '-1px' }}>
          plotted<span style={{ color: C.accentHi }}>.</span>
        </div>
        <div style={{ marginTop: 16, width: 24, height: 24, borderRadius: '50%',
          border: `2px solid ${C.border}`, borderTopColor: C.accent,
          animation: 'spin 0.8s linear infinite', margin: '16px auto 0' }}/>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (error || !data) {
    const errLang = 'fr';
    const Et = (k) => tShare(errLang, k);
    return (
      <div style={{ background: C.bg, minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24 }}>
        <div style={{ fontSize: 40 }}>🔗</div>
        <div style={{ color: C.text, fontSize: 18, fontWeight: 700 }}>{Et('profile.shareViewExpired')}</div>
        <button onClick={goSignup} style={ctaBtn}>{Et('profile.shareViewCta')} →</button>
      </div>
    );
  }

  const payload     = data.payload || {};
  const mode        = payload.mode || 'list';
  const ownerName   = payload.ownerName || '';
  const lang        = payload.lang || 'fr';
  const T           = (key) => tShare(lang, key);
  const createdAt   = new Date(data.created_at).toLocaleDateString(
    lang === 'zh' ? 'zh-CN' : lang === 'es' ? 'es-ES' : lang === 'en' ? 'en-GB' : 'fr-FR',
    { day: 'numeric', month: 'long', year: 'numeric' });

  const isTrip = mode === 'trip';
  const theme  = isTrip ? CL : C;
  const pageTitle = (payload.dest?.name || ownerName || 'Plotted').replace(/"/g, "'");

  return (
    <div className="share-root" style={{ background: theme.bg, minHeight: '100vh',
      fontFamily: "'DM Sans',system-ui,sans-serif",
      paddingBottom: 100, maxWidth: 600, margin: '0 auto' }}>
      <style>{`
        @keyframes spin    { to { transform: rotate(360deg) } }
        @keyframes fadeUp  { from { opacity:0; transform:translateY(20px) } to { opacity:1; transform:translateY(0) } }
        .fade-up { animation: fadeUp 0.6s ease both; }
        @page {
          margin: 20mm 14mm 18mm;
          @top-left    { content: "plotted."; font-size: 10pt; font-weight: 900; color: #0f1f2e; font-family: system-ui; }
          @top-right   { content: "${pageTitle}"; font-size: 9pt; color: #8fa8b4; font-family: system-ui; }
          @bottom-left { content: "plotted.travel"; font-size: 9pt; color: #8fa8b4; font-family: system-ui; }
          @bottom-right{ content: counter(page); font-size: 9pt; color: #888; font-family: system-ui; }
        }
        @media print {
          .no-print { display: none !important; }
          .page-break { page-break-before: always; }
          .no-break { page-break-inside: avoid; }
          .print-open { display: block !important; max-height: none !important; overflow: visible !important; }
          .print-hide-btn { display: none !important; }
          .no-print-map { display: none !important; }
          body { background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .share-root { padding-bottom: 0 !important; min-height: unset !important; max-width: 100% !important; }
        }
      `}</style>

      {/* ── Sticky nav ─── */}
      <div className="no-print" style={{ background: isTrip ? 'rgba(255,255,255,0.95)' : 'rgba(7,16,26,0.92)', backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${theme.border}`, padding: '14px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ fontSize: 22, fontWeight: 900, color: theme.text, letterSpacing: '-0.8px' }}>
          plotted<span style={{ color: theme.accent }}>.</span>
        </div>
        <button onClick={goSignup} style={{ background: `linear-gradient(135deg,#006F87,${C.accent})`,
          border: 'none', borderRadius: 24, padding: '9px 20px',
          color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(0,158,194,0.3)' }}>
          {mode === 'trip' ? T('profile.shareViewCtaTrip') : T('profile.shareViewCta')}
        </button>
      </div>

      {/* ── Modes ─── */}
      {mode === 'topn' && <TopNView payload={payload} ownerName={ownerName} createdAt={createdAt} T={T} token={token} />}
      {mode === 'list' && <ListView payload={payload} ownerName={ownerName} createdAt={createdAt} T={T} />}
      {mode === 'trip' && <TripView payload={payload} ownerName={ownerName} createdAt={createdAt} T={T} cl={CL} />}

      {/* ── CTA — masqué pour le partage de voyage (level 3) ─── */}
      {mode !== 'trip' && <div className="no-print" style={{ margin: '36px 16px 0', borderRadius: 24, overflow: 'hidden',
        background: `linear-gradient(135deg,rgba(0,111,135,0.18),rgba(0,158,194,0.08))`,
        border: `1px solid rgba(0,158,194,0.25)` }}>
        <div style={{ padding: '28px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 900, color: C.text, letterSpacing: '-0.6px', marginBottom: 10, lineHeight: 1.25 }}>
            {ownerName
              ? <>{T('profile.shareViewCtaCard')(ownerName)} {payload.planCount || payload.dests?.filter(d=>d.inPlan).length || 5}.<br/>{T('profile.shareViewCtaYou')}</>
              : T('profile.shareViewCtaCardAnon')}
          </div>
          <div style={{ fontSize: 13, color: C.sub, lineHeight: 1.7, marginBottom: 22 }}>
            {T('profile.shareViewCtaBody')}
          </div>
          <button onClick={goSignup} style={ctaBtn}>
            {T('profile.shareViewCtaBtn')}
          </button>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 12 }}>{T('profile.shareViewCtaSub')}</div>
        </div>
        <div style={{ borderTop: `1px solid ${C.border}`, padding: '12px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <div style={{ width: 5, height: 5, borderRadius: '50%', background: C.accentHi }}/>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>plotted.travel</div>
        </div>
      </div>}
    </div>
  );
}

// ─── TopN view : world map with pinned polaroids ─────────────────────────────

function TopNView({ payload, ownerName, createdAt, T, token }) {
  const dests = payload.dests || [];
  const n = payload.planCount || dests.filter(d => d.inPlan).length;
  const [sel, setSel] = useState(null);

  const goVoyeur = () => {
    window.location.href = window.location.origin + '/?shared=' + token;
  };

  return (
    <div>
      {/* Header */}
      <div style={{ textAlign: 'center', padding: '16px 16px 8px' }}>
        <div style={{ fontSize: 12, color: C.sub, fontWeight: 700,
          letterSpacing: 1.5, textTransform: 'uppercase' }}>
          {T('profile.shareViewTopNOf')(n, ownerName)}
        </div>
        <div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>{createdAt}</div>
      </div>

      {/* Plan dests — polaroid grid */}
      {dests.filter(d => d.inPlan).length > 0 && (
        <div style={{
          padding: '4px 16px 16px',
          backgroundImage: 'radial-gradient(circle, rgba(77,212,240,0.06) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 14px' }}>
            {dests.filter(d => d.inPlan).map((dest, i) => {
              const rot = POLA_ROTS[i % POLA_ROTS.length];
              const mt  = POLA_MTS[i % POLA_MTS.length];
              const isLast = i === dests.filter(d => d.inPlan).length - 1 && dests.filter(d => d.inPlan).length % 2 === 1;
              return (
                <div key={i} style={{
                  marginTop: mt * 0.6,
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  ...(isLast ? { gridColumn: '1 / -1' } : {}),
                }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#e74c3c',
                    boxShadow: '0 1px 4px rgba(231,76,60,0.7)', marginBottom: 2 }}/>
                  <div style={{
                    position: 'relative', background: 'white', padding: '4px 4px 0', borderRadius: 3,
                    boxShadow: '2px 5px 16px rgba(0,0,0,0.55)',
                    transform: `rotate(${rot}deg)`, transformOrigin: 'top center',
                    width: isLast ? '40%' : '100%',
                  }}>
                    {dest.rank === 1 && (
                      <div style={{
                        position: 'absolute', top: 6, left: 6, zIndex: 2,
                        width: 17, height: 17, borderRadius: 9,
                        background: 'linear-gradient(135deg,#F4A928,#fb923c)',
                        color: '#1a1a1a', fontSize: 7, fontWeight: 900,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>#{dest.rank}</div>
                    )}
                    <div style={{ width: '100%', aspectRatio: '4/3', overflow: 'hidden' }}>
                      <img src={dest.photoUrl} alt={dest.name}
                        onError={e => { e.target.style.opacity = '0.3'; }}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}/>
                    </div>
                    <div style={{
                      textAlign: 'center', padding: '3px 2px 4px',
                      fontFamily: "'Caveat','Comic Sans MS',cursive",
                      fontSize: 16, fontWeight: 700, color: '#1a1a1a', lineHeight: 1,
                    }}>{dest.name}</div>
                  </div>
                  {dest.minPrice && (
                    <div style={{ marginTop: 5, transform: `rotate(${-rot * 0.4}deg)`, textAlign: 'center' }}>
                      <span style={{ background: 'rgba(244,169,40,0.88)', color: '#07101a',
                        fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 20 }}>
                        €{Math.round(dest.minPrice)}
                      </span>
                      {dest.slotFrom && (
                        <div style={{ fontSize: 8, color: C.muted, marginTop: 2, fontWeight: 600 }}>
                          {dest.slotFrom}{dest.slotTo ? ` – ${dest.slotTo}` : ''}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Explorer button — above map */}
      <div style={{ padding: '8px 16px 8px', textAlign: 'center' }}>
        <button onClick={goVoyeur} style={{ ...ctaBtn, fontSize: 14, padding: '13px 32px' }}>
          {ownerName ? `Explorer la carte de ${ownerName} →` : 'Explorer cette carte →'}
        </button>
      </div>

      {/* Map */}
      <div style={{ position: 'relative', background: '#060e1a', overflow: 'hidden' }}
        onClick={() => setSel(null)}>
        <ComposableMap
          projection="geoNaturalEarth1"
          projectionConfig={{ scale: 145 }}
          style={{ width: '100%', height: 'auto', display: 'block' }}>
          <Geographies geography={GEO_URL}>
            {({ geographies }) => geographies.map(geo => (
              <Geography key={geo.rsmKey} geography={geo}
                style={{
                  default: { fill: '#162436', stroke: '#0a1626', strokeWidth: 0.4, outline: 'none' },
                  hover:   { fill: '#162436', outline: 'none' },
                  pressed: { fill: '#162436', outline: 'none' },
                }}/>
            ))}
          </Geographies>

          {/* Render bucket list first (underneath) */}
          {dests.filter(d => !d.inPlan && d.lat && d.lng).map((dest, i) => {
            const rot = POLA_ROTS[(i + 5) % POLA_ROTS.length] * 0.5;
            const W = 33; const H = 26;
            return (
              <Marker key={'b' + i} coordinates={[dest.lng, dest.lat]}
                onClick={e => { e.stopPropagation(); setSel(dest); }}>
                <g style={{ cursor: 'pointer' }}>
                  <line x1={0} y1={-1} x2={0} y2={5} stroke={C.muted} strokeWidth={1.2}/>
                  <circle cx={0} cy={-2} r={2.5} fill={C.muted} stroke="white" strokeWidth={0.6}/>
                  <g transform={`rotate(${rot}) translate(${-W/2 - 2}, ${-H - 9})`}>
                    <rect width={W + 4} height={H + 5} fill="white" rx={1} ry={1}
                      style={{ filter: 'drop-shadow(1px 2px 4px rgba(0,0,0,0.5))' }}/>
                    <image href={dest.photoUrl} x={2} y={2} width={W} height={H - 2}
                      preserveAspectRatio="xMidYMid slice"
                      style={{ opacity: 0.8 }}/>
                    <text x={(W+4)/2} y={H + 2.5} textAnchor="middle" fontSize={4.5}
                      fontFamily="Caveat,cursive" fontWeight="700" fill="#1a1a1a">
                      {dest.name.length > 9 ? dest.name.slice(0, 9) + '…' : dest.name}
                    </text>
                  </g>
                </g>
              </Marker>
            );
          })}

          {/* Plan dests on top — larger, red pin */}
          {dests.filter(d => d.inPlan && d.lat && d.lng).map((dest, i) => {
            const rot = POLA_ROTS[i % POLA_ROTS.length];
            const W = 44; const H = 35;
            return (
              <Marker key={'p' + i} coordinates={[dest.lng, dest.lat]}
                onClick={e => { e.stopPropagation(); setSel(dest); }}>
                <g style={{ cursor: 'pointer' }}>
                  <line x1={0} y1={-1} x2={0} y2={7} stroke="#e74c3c" strokeWidth={1.5}/>
                  <circle cx={0} cy={-2} r={3.5} fill="#e74c3c" stroke="white" strokeWidth={0.8}/>
                  <g transform={`rotate(${rot}) translate(${-W/2 - 2}, ${-H - 13})`}>
                    <rect width={W + 4} height={H + 6} fill="white" rx={1.5} ry={1.5}
                      style={{ filter: 'drop-shadow(2px 4px 8px rgba(0,0,0,0.65))' }}/>
                    {dest.rank === 1 && (
                      <rect x={1} y={1} width={W + 2} height={H + 4} fill="none" rx={1}
                        stroke="#F4A928" strokeWidth={1.5}/>
                    )}
                    <image href={dest.photoUrl} x={2} y={2} width={W} height={H - 2}
                      preserveAspectRatio="xMidYMid slice"/>
                    <text x={(W+4)/2} y={H + 3.5} textAnchor="middle" fontSize={6}
                      fontFamily="Caveat,cursive" fontWeight="700" fill="#1a1a1a">
                      {dest.name.length > 9 ? dest.name.slice(0, 9) + '…' : dest.name}
                    </text>
                    {dest.minPrice && (
                      <text x={(W+4)/2} y={H - 1} textAnchor="middle" fontSize={4}
                        fontFamily="system-ui,sans-serif" fontWeight="800" fill="#1a1a1a">
                        €{Math.round(dest.minPrice)}
                      </text>
                    )}
                  </g>
                </g>
              </Marker>
            );
          })}
        </ComposableMap>

        {/* Selected dest card */}
        {sel && (
          <div style={{
            position: 'absolute', bottom: 12, left: 12, right: 12,
            background: 'rgba(7,16,26,0.96)', backdropFilter: 'blur(20px)',
            border: `1px solid ${C.border}`, borderRadius: 16, padding: 14,
            display: 'flex', alignItems: 'center', gap: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ width: 56, height: 42, borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
              <img src={sel.photoUrl} alt={sel.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.text,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {sel.name}
              </div>
              <div style={{ fontSize: 11, color: C.sub }}>{sel.country}</div>
            </div>
            {sel.minPrice && (
              <div style={{ background: 'rgba(244,169,40,0.85)', color: '#07101a',
                fontSize: 12, fontWeight: 800, padding: '4px 10px', borderRadius: 20, flexShrink: 0 }}>
                €{Math.round(sel.minPrice)}
              </div>
            )}
          </div>
        )}

        {/* Ocean tint overlay */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'linear-gradient(to bottom, rgba(6,14,26,0.15), transparent 20%, transparent 80%, rgba(6,14,26,0.4))' }}/>
      </div>

    </div>
  );
}

// ─── List view : polaroid + slots ────────────────────────────────────────────

function ListView({ payload, ownerName, createdAt, T }) {
  const dest    = payload.dest || {};
  const slots   = payload.slots || [];
  const guide   = payload.guide || null;
  const photos  = payload.photos || [];
  const heroPhoto = photos[0] || '';

  const cheapest = slots.length ? Math.min(...slots.filter(s => s.price).map(s => s.price)) : null;

  return (
    <div>
      {/* Header : polaroid + info */}
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', padding: '20px 16px 16px' }}>
        {/* Polaroid — format identique à DestPhotoBanner */}
        <div style={{ flexShrink: 0, width: '40%' }}>
          <div style={{
            background: 'white', padding: '4px 4px 0', borderRadius: 3,
            boxShadow: '4px 10px 28px rgba(0,0,0,0.58)',
            transform: 'rotate(-2deg)', transformOrigin: 'top left',
          }}>
            <div style={{ width: '100%', aspectRatio: '4/3', overflow: 'hidden' }}>
              <img src={heroPhoto} alt={dest.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                onError={e => { e.target.src = 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=600&q=80'; }}/>
            </div>
            <div style={{
              textAlign: 'center', padding: '4px 3px 6px',
              fontFamily: "'Caveat','Comic Sans MS',cursive",
              fontSize: 26, fontWeight: 700, color: '#1a1a1a', lineHeight: 1,
            }}>{dest.name}</div>
          </div>
        </div>

        {/* Right info */}
        <div style={{ flex: 1, paddingTop: 4 }}>
          {ownerName && (
            <div style={{ fontSize: 10, color: C.muted, fontWeight: 700,
              letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>
              {T('profile.shareViewSlotsOf')(ownerName)}
            </div>
          )}
          <div style={{ fontSize: 22, fontWeight: 900, color: C.text,
            letterSpacing: '-0.5px', lineHeight: 1.1 }}>
            {dest.name}
          </div>
          {cheapest && (
            <div style={{ marginTop: 12, display: 'inline-block',
              background: 'rgba(244,169,40,0.85)', color: '#07101a',
              fontSize: 13, fontWeight: 800, padding: '4px 12px', borderRadius: 20 }}>
              {T('profile.shareViewFrom')} €{Math.round(cheapest)}
            </div>
          )}
          <div style={{ marginTop: 8, fontSize: 11, color: C.muted }}>Prix au {createdAt}</div>
        </div>
      </div>

      {/* Slots */}
      {slots.length > 0 && (
        <div className="fade-up" style={{ padding: '0 16px', marginTop: 4 }}>
          <SectionTitle label={T('profile.shareViewFlights')} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {slots.map((s, i) => (
              <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`,
                borderRadius: 14, padding: '12px 14px',
                display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 28, height: 28, borderRadius: 14, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 800,
                  background: i === 0 ? 'linear-gradient(135deg,#F4A928,#fb923c)' : 'rgba(0,158,194,0.15)',
                  color: i === 0 ? '#07101a' : C.accentHi }}>
                  #{i+1}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                    {s.from} – {s.to}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    {s.type && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 20,
                        background: `${SLOT_TYPE_COLORS[s.type] || C.accentHi}22`,
                        color: SLOT_TYPE_COLORS[s.type] || C.accentHi }}>
                        {SLOT_TYPE_LABELS_FR[s.type] || s.type}
                      </span>
                    )}
                    <span style={{ fontSize: 11, color: C.muted }}>{s.nights}n</span>
                    {s.airline && <span style={{ fontSize: 11, color: C.sub }}>· {s.airline}</span>}
                    {!s.isReal && <span style={{ fontSize: 10, color: C.muted, fontStyle: 'italic' }}>{T('profile.shareViewEstimated')}</span>}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  {s.price ? (
                    <>
                      <div style={{ fontSize: 16, fontWeight: 900, color: i === 0 ? C.gold : C.accentHi }}>
                        €{Math.round(s.price)}
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: 11, color: C.muted, fontStyle: 'italic' }}>~</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Guide */}
      <GuideBlock guide={guide} destName={dest.name} T={T} />
    </div>
  );
}

// ─── Trip view ───────────────────────────────────────────────────────────────

function TripView({ payload, ownerName, createdAt, T, cl = C }) {
  const dest       = payload.dest || {};
  const photos     = payload.photos || [];
  const trip       = payload.trip || null;
  const guide      = payload.guide || null;
  const itinerary  = payload.itinerary || null;
  const travelers  = payload.travelers || [];
  const heroPhoto  = photos[0] || '';

  return (
    <div>
      {/* Header — polaroid + trip info */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', padding: '20px 16px 12px' }}>
        {/* Polaroid */}
        <div style={{ flexShrink: 0, width: '40%' }}>
          <div style={{ background: 'white', padding: '4px 4px 0', borderRadius: 3,
            boxShadow: '4px 10px 28px rgba(0,0,0,0.58)',
            transform: 'rotate(-2deg)', transformOrigin: 'top left' }}>
            <div style={{ width: '100%', aspectRatio: '4/3', overflow: 'hidden' }}>
              <img src={heroPhoto} alt={dest.name}
                onError={e => { e.target.src = 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=600&q=80'; }}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}/>
            </div>
            <div style={{ textAlign: 'center', padding: '4px 3px 6px',
              fontFamily: "'Caveat','Comic Sans MS',cursive",
              fontSize: 22, fontWeight: 700, color: '#1a1a1a', lineHeight: 1 }}>{dest.name}</div>
          </div>
        </div>

        {/* Right info */}
        <div style={{ flex: 1, paddingTop: 4, paddingLeft: 6 }}>
          <div style={{ fontSize: 22, fontWeight: 900, color: cl.text,
            letterSpacing: '-0.5px', lineHeight: 1.1 }}>
            {T('profile.shareViewTripTo')(dest.name)}
          </div>
          {travelers.length > 0 && (
            <div style={{ marginTop: 6, fontSize: 12, color: cl.sub }}>
              {travelers.join(' · ')}
            </div>
          )}
          {trip && (
            <div style={{ marginTop: 8, fontSize: 12, color: cl.sub }}>
              {trip.from} – {trip.to} · {trip.nights}n
            </div>
          )}
        </div>
      </div>

      {/* Compact trip summary — no icons */}
      {trip && (
        <div className="no-break" style={{ padding: '0 16px 4px' }}>
          <div style={{ background: cl.card, border: `1px solid ${cl.border}`, borderRadius: 14, padding: '10px 14px',
            display: 'flex', flexDirection: 'column', gap: 8 }}>
            {trip.flight && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: cl.text }}>{trip.flight.airline}</div>
                  <div style={{ fontSize: 11, color: cl.sub }}>
                    {trip.flight.stops === 0 ? T('profile.shareViewDirect') : T('profile.shareViewStop')(trip.flight.stops)}
                    {trip.flight.durationMins ? ` · ${Math.floor(trip.flight.durationMins/60)}h${String(trip.flight.durationMins%60).padStart(2,'0')}` : ''}
                    {trip.flight.departureTime ? ` · ${trip.flight.departureTime.substring(11,16)}→${trip.flight.arrivalTime?.substring(11,16)}` : ''}
                  </div>
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color: cl.gold, flexShrink: 0 }}>
                  {trip.flight.isReal ? '' : '~'}€{Math.round(trip.flight.price)}
                </div>
              </div>
            )}
            {trip.flight && trip.hotel && <div style={{ height: 1, background: cl.border }}/>}
            {trip.hotel && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: cl.text }}>{trip.hotel.name}</div>
                  <div style={{ fontSize: 11, color: cl.sub }}>
                    {'★'.repeat(trip.hotel.stars || 4)} · {trip.hotel.rating}/10
                    {trip.hotel.cancelFree ? ` · ${T('profile.shareViewFreeCancel')}` : ''}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: cl.accentHi }}>€{Math.round(trip.hotel.price)}</div>
                  {trip.hotel.pricePerNight && <div style={{ fontSize: 10, color: cl.muted }}>€{Math.round(trip.hotel.pricePerNight)}/n</div>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Itinerary */}
      <ItineraryBlock itinerary={itinerary} T={T} cl={cl} />

      {/* Guide — star content */}
      <GuideBlock guide={guide} destName={dest.name} T={T} cl={cl} />
    </div>
  );
}

// ─── Shared sub-components ───────────────────────────────────────────────────

// Wikidata photo for a guide item
function HighlightPhoto({ itemName, itemNameEn, destName, side = 'left' }) {
  const [photo, setPhoto] = useState(null);
  useEffect(() => {
    const BAD = /flag|coat|logo|blason|emblem|icon|seal|arms|wappen|shield|banner|locator|map|carte|plan\b/i;
    const tryWiki = async (lang, title) => {
      try {
        const r = await fetch(`https://www.wikidata.org/w/api.php?action=wbgetentities&sites=${lang}wiki&titles=${encodeURIComponent(title)}&props=claims&format=json&origin=*`);
        const d = await r.json();
        const entity = Object.values(d.entities || {}).find(e => e.id && e.id !== '-1');
        const p18 = entity?.claims?.P18?.[0]?.mainsnak?.datavalue?.value;
        if (p18 && !BAD.test(p18)) return `https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(p18.replace(/ /g,'_'))}?width=600`;
      } catch {}
      return null;
    };
    const nameEn = itemNameEn || itemName;
    tryWiki('en', nameEn)
      .then(u => u || tryWiki('en', `${nameEn} ${destName}`))
      .then(u => { if (u) setPhoto(u); });
  }, [itemName, itemNameEn, destName]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!photo) return null;
  return (
    <div style={{
      float: side, width: '38%', aspectRatio: '4/3', overflow: 'hidden', borderRadius: 6,
      marginRight: side === 'left' ? 10 : 0,
      marginLeft:  side === 'right' ? 10 : 0,
      marginBottom: 6,
    }}>
      <img src={photo} alt={itemName}
        onError={e => { e.target.parentNode.style.display = 'none'; }}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}/>
    </div>
  );
}

// Leaflet map with numbered markers
function CityMap({ destName, items = [], cl = C }) {
  const mapDivRef = useRef(null);
  const mapRef    = useRef(null);
  const [geocoded, setGeocoded] = useState([]);

  // Geocode city + all items (with 250ms delay between requests to respect Nominatim policy)
  useEffect(() => {
    let cancelled = false;
    const geocode = async (q, viewbox = null) => {
      try {
        let url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=1`;
        if (viewbox) url += `&viewbox=${viewbox}&bounded=1`;
        const r = await fetch(url);
        const d = await r.json();
        return d[0] ? { lat: parseFloat(d[0].lat), lng: parseFloat(d[0].lon) } : null;
      } catch { return null; }
    };
    const run = async () => {
      const cityCoords = await geocode(destName);
      if (cancelled || !cityCoords) return;
      const results = [{ num: 0, name: destName, ...cityCoords }]; // city center as anchor
      // Viewbox ~0.5° (~50km) around city center — large enough for day-trip sites (Tivoli from Rome etc.)
      // City name in query ("Villa d'Este Rome") already avoids cross-country false positives
      const { lat, lng } = cityCoords;
      const vb = `${lng - 0.5},${lat + 0.5},${lng + 0.5},${lat - 0.5}`;
      for (const item of items) {
        if (cancelled) break;
        await new Promise(r => setTimeout(r, 250));
        const name = item.name_en || item.name;
        const coords = await geocode(`${name} ${destName}`, vb)
          || await geocode(name, vb)
          || await geocode(`${name} ${destName}`);
        if (coords) results.push({ ...item, ...coords });
      }
      if (!cancelled) setGeocoded(results);
    };
    run();
    return () => { cancelled = true; };
  }, [destName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Init Leaflet map once we have geocoded data
  useEffect(() => {
    if (!mapDivRef.current || geocoded.length === 0) return;
    const city = geocoded[0];
    const markers = geocoded.slice(1);

    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }

    const map = L.map(mapDivRef.current, { zoomControl: true, scrollWheelZoom: false })
      .setView([city.lat, city.lng], 13);
    mapRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
    }).addTo(map);

    // Only plot markers within ~15km of city center to keep map usable
    const nearby = markers.filter(m =>
      Math.abs(m.lat - city.lat) < 0.14 && Math.abs(m.lng - city.lng) < 0.18
    );
    nearby.forEach(item => {
      const icon = L.divIcon({
        className: '',
        html: `<div style="width:18px;height:18px;border-radius:50%;background:#009EC2;color:#fff;display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:800;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);line-height:1">${item.num}</div>`,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      });
      L.marker([item.lat, item.lng], { icon })
        .addTo(map)
        .bindPopup(`<b>${item.name}</b>`);
    });

    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, [geocoded]);

  return (
    <div className="no-print-map" style={{ marginTop: 28, borderRadius: 16, overflow: 'hidden', border: `1px solid ${cl.border}` }}>
      <div style={{ fontSize: 10, color: cl.muted, padding: '6px 12px', background: cl.card,
        fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>Plan</div>
      <div ref={mapDivRef} style={{ height: 320 }}/>
    </div>
  );
}

function ItineraryBlock({ itinerary, T, cl = C }) {
  const days = itinerary?.days || [];
  const [openDays, setOpenDays] = useState(() => new Set(days.map((_, i) => i)));
  if (!days.length) return null;

  const toggle = (i) => setOpenDays(prev => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });

  return (
    <div style={{ padding: '0 16px', marginBottom: 32 }}>
      <div style={{ marginTop: 36, marginBottom: 20, textAlign: 'center' }}>
        <div style={{ fontSize: 10, color: cl.muted, fontWeight: 800, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 6 }}>{T('profile.shareViewItineraryTitle')}</div>
        <div style={{ width: 28, height: 2, background: cl.accent, margin: '0 auto', borderRadius: 1 }}/>
      </div>
      {days.map((day, di) => (
        <div key={di} className="no-break" style={{ marginBottom: 8 }}>
          <button className="print-hide-btn" onClick={() => toggle(di)} style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: openDays.has(di) ? cl.accent : cl.card,
            border: `1px solid ${openDays.has(di) ? cl.accent : cl.border}`,
            borderRadius: openDays.has(di) ? '12px 12px 0 0' : 12,
            padding: '10px 14px', cursor: 'pointer',
          }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: openDays.has(di) ? '#fff' : cl.text }}>
              {T('profile.shareViewDay')(day.day)}
            </span>
            <span style={{ fontSize: 16, color: openDays.has(di) ? '#fff' : cl.muted, lineHeight: 1 }}>
              {openDays.has(di) ? '−' : '+'}
            </span>
          </button>

          <div className="print-open" style={{ display: openDays.has(di) ? 'block' : 'none',
            border: `1px solid ${cl.border}`, borderTop: 'none',
            borderRadius: '0 0 12px 12px', overflow: 'hidden' }}>
            {day.morning && <HalfDay label={T('profile.shareViewMorning')} slot={day.morning} cl={cl} />}
            {day.lunch?.length > 0 && <MealOptions label={T('profile.shareViewLunch')} options={day.lunch} cl={cl} />}
            {day.afternoon && <HalfDay label={T('profile.shareViewAfternoon')} slot={day.afternoon} cl={cl} />}
            {day.evening && <HalfDay label={T('profile.shareViewEvening')} slot={day.evening} cl={cl} />}
            {day.dinner?.length > 0 && <MealOptions label={T('profile.shareViewDinner')} options={day.dinner} cl={cl} />}
          </div>
        </div>
      ))}
    </div>
  );
}

function HalfDay({ label, slot, cl = C }) {
  return (
    <div style={{ padding: '12px 14px', borderBottom: `1px solid ${cl.border}` }}>
      <div style={{ fontSize: 9, color: cl.muted, fontWeight: 800, letterSpacing: 1.5,
        textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginBottom: 3 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: cl.text }}>{slot.title}</span>
        {slot.place && (
          <span style={{ fontSize: 10, color: cl.muted, fontWeight: 600,
            background: cl.card, border: `1px solid ${cl.border}`,
            borderRadius: 20, padding: '1px 7px' }}>{slot.place}</span>
        )}
      </div>
      {slot.desc && <div style={{ fontSize: 12, color: cl.sub, lineHeight: 1.5, paddingLeft: 21 }}>{slot.desc}</div>}
    </div>
  );
}

function MealOptions({ label, options, cl = C }) {
  return (
    <div style={{ padding: '10px 14px', borderBottom: `1px solid ${cl.border}`,
      background: cl.bg === '#ffffff' ? '#f0f4f6' : 'rgba(0,0,0,0.15)' }}>
      <div style={{ fontSize: 9, color: cl.muted, fontWeight: 800, letterSpacing: 1.5,
        textTransform: 'uppercase', marginBottom: 7 }}>{label}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {options.map((o, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: cl.accent,
              minWidth: 14, flexShrink: 0 }}>{i + 1}.</span>
            <div>
              <span style={{ fontSize: 12, fontWeight: 700, color: cl.text }}>{o.name}</span>
              {o.cuisine && <span style={{ fontSize: 11, color: cl.muted }}> · {o.cuisine}</span>}
              {o.note && <div style={{ fontSize: 11, color: cl.sub, lineHeight: 1.4 }}>{o.note}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PracticalBlock({ practical, note, T, cl = C }) {
  if (!practical) return null;
  const rows = [
    { label: T('profile.shareViewLabelCurrency'),    val: practical.currency },
    { label: T('profile.shareViewLabelPhone'),        val: practical.phone_prefix },
    { label: T('profile.shareViewLabelElectricity'),  val: practical.electricity },
    { label: T('profile.shareViewLabelLanguage'),     val: practical.language },
    { label: T('profile.shareViewLabelTimezone'),     val: practical.timezone },
    { label: T('profile.shareViewLabelWater'),        val: practical.tap_water },
    { label: T('profile.shareViewLabelTipping'),      val: practical.tipping },
    { label: T('profile.shareViewLabelHolidays'),     val: practical.public_holidays },
    { label: T('profile.shareViewLabelSales'),        val: practical.sales },
  ].filter(r => r.val);
  if (!rows.length && !note) return null;
  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ marginBottom: 8, paddingBottom: 6, borderBottom: `1px solid ${cl.border}` }}>
        <span style={{ fontSize: 10, color: cl.accent, fontWeight: 800, letterSpacing: 2.5, textTransform: 'uppercase' }}>
          {T('profile.shareViewPracticalTitle')}
        </span>
      </div>
      {rows.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {rows.map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 8,
              padding: '5px 0', borderBottom: i < rows.length - 1 ? `1px solid ${cl.border}` : 'none' }}>
              <span style={{ fontSize: 10, color: cl.muted, fontWeight: 700, letterSpacing: 0.5,
                textTransform: 'uppercase', minWidth: 88, flexShrink: 0 }}>{r.label}</span>
              <span style={{ fontSize: 12, color: cl.text, lineHeight: 1.4 }}>{r.val}</span>
            </div>
          ))}
        </div>
      )}
      {note && (
        <div style={{ marginTop: 14, padding: '10px 12px', background: cl.card,
          border: `1px solid ${cl.border}`, borderRadius: 10 }}>
          <div style={{ fontSize: 10, color: cl.accent, fontWeight: 800, letterSpacing: 2,
            textTransform: 'uppercase', marginBottom: 5 }}>{T('profile.shareViewNoteTitle')}</div>
          <div style={{ fontSize: 12, color: cl.text, lineHeight: 1.5 }}>{note}</div>
        </div>
      )}
    </div>
  );
}

function PhrasebookBlock({ phrasebook, T, cl = C }) {
  if (!phrasebook?.length) return null;
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ marginBottom: 8, paddingBottom: 6, borderBottom: `1px solid ${cl.border}` }}>
        <span style={{ fontSize: 10, color: cl.accent, fontWeight: 800, letterSpacing: 2.5, textTransform: 'uppercase' }}>
          {T('profile.shareViewPhrasebookTitle')}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {phrasebook.map((row, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto',
            alignItems: 'baseline', gap: 8, padding: '5px 0',
            borderBottom: i < phrasebook.length - 1 ? `1px solid ${cl.border}` : 'none' }}>
            <span style={{ fontSize: 12, color: cl.sub }}>{row.phrase}</span>
            <span style={{ fontSize: 12, color: cl.text, fontWeight: 600 }}>{row.local}</span>
            <span style={{ fontSize: 11, color: cl.muted, fontStyle: 'italic' }}>{row.phonetic}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function GuideBlock({ guide, destName, T, cl = C }) {
  if (!guide) return null;
  const hasContent = guide.highlights?.length || guide.hidden_gems?.length || guide.bars?.length || guide.restaurants?.length;
  if (!hasContent) return null;

  // Sequential numbering across all sections
  let n = 0;
  const highlights  = (guide.highlights  || []).slice(0, 3).map(item => ({ ...item, num: ++n }));
  const gems        = (guide.hidden_gems || []).map(item => ({ ...item, num: ++n }));
  const bars        = (guide.bars        || []).map(item => ({ ...item, num: ++n }));
  const restaurants = (guide.restaurants || []).map(item => ({ ...item, num: ++n }));

  return (
    <div style={{ padding: '0 16px', paddingBottom: 32 }}>
      {/* Title */}
      <div style={{ marginTop: 36, marginBottom: 28, textAlign: 'center' }}>
        <div style={{ fontSize: 10, color: cl.muted, fontWeight: 800, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 6 }}>Découvrir</div>
        <div style={{ fontSize: 32, fontWeight: 900, color: cl.text, letterSpacing: '-1.5px', lineHeight: 1 }}>{destName}</div>
        <div style={{ width: 28, height: 2, background: cl.accent, margin: '10px auto 0', borderRadius: 1 }}/>
      </div>

      {highlights.length > 0 && (
        <GuideSection title="À ne pas rater" accent={cl.accentHi} items={highlights} cl={cl}
          renderItem={item => {
            const side = item.num % 2 === 1 ? 'left' : 'right';
            return (
              <div>
                <HighlightPhoto itemName={item.name} itemNameEn={item.name_en} destName={destName} side={side} />
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: cl.text }}>{item.name}</span>
                  <span style={{ fontSize: 11, color: cl.muted }}>({item.num})</span>
                </div>
                <div style={{ fontSize: 12, color: cl.sub, marginTop: 3, lineHeight: 1.5 }}>{item.desc}</div>
                <div style={{ clear: 'both' }}/>
              </div>
            );
          }}/>
      )}
      {gems.length > 0 && (
        <GuideSection title="Loin des sentiers battus" accent={cl.gold} items={gems} cl={cl}
          renderItem={item => {
            const side = item.num % 2 === 1 ? 'left' : 'right';
            return (
              <div>
                <HighlightPhoto itemName={item.name} itemNameEn={item.name_en} destName={destName} side={side} />
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: cl.text }}>{item.name}</span>
                  <span style={{ fontSize: 11, color: cl.muted }}>({item.num})</span>
                </div>
                <div style={{ fontSize: 12, color: cl.sub, marginTop: 3, lineHeight: 1.5 }}>{item.desc}</div>
                <div style={{ clear: 'both' }}/>
              </div>
            );
          }}/>
      )}
      {bars.length > 0 && (
        <GuideSection title="Bars & terrasses" accent={cl.rose} items={bars} cl={cl}
          renderItem={item => (
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: cl.text }}>{item.name}</span>
                <span style={{ fontSize: 11, color: cl.muted }}>({item.num})</span>
                <PriceTag price={item.price} cl={cl}/>
              </div>
              <div style={{ fontSize: 12, color: cl.sub, marginTop: 2 }}>{item.vibe}</div>
            </div>
          )}/>
      )}
      {restaurants.length > 0 && (
        <GuideSection title="Bonnes adresses" accent={cl.green} items={restaurants} cl={cl}
          renderItem={item => (
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: cl.text }}>{item.name}</span>
                <span style={{ fontSize: 11, color: cl.muted }}>({item.num})</span>
                <PriceTag price={item.price} cl={cl}/>
              </div>
              <div style={{ fontSize: 11, color: cl.muted, marginTop: 1 }}>{item.cuisine}</div>
              <div style={{ fontSize: 12, color: cl.sub, marginTop: 2 }}>{item.vibe}</div>
            </div>
          )}/>
      )}

      <CityMap destName={destName} items={[...highlights, ...gems]} cl={cl} />

      <PracticalBlock practical={guide.practical} note={guide.note} T={T} cl={cl} />
      <PhrasebookBlock phrasebook={guide.phrasebook} T={T} cl={cl} />
    </div>
  );
}

function SectionTitle({ icon, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <span style={{ fontSize: 16 }}>{icon}</span>
      <span style={{ fontSize: 13, fontWeight: 800, color: C.sub, letterSpacing: 0.3, textTransform: 'uppercase' }}>{label}</span>
    </div>
  );
}

function GuideSection({ title, items, accent, renderItem, cl = C }) {
  const resolvedAccent = accent ?? cl.accentHi;
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${cl.border}` }}>
        <span style={{ fontSize: 10, color: resolvedAccent, fontWeight: 800, letterSpacing: 2.5, textTransform: 'uppercase' }}>
          {title}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map((item, i) => (
          <div key={i} className="no-break" style={{ background: cl.card, border: `1px solid ${cl.border}`,
            borderRadius: 12, padding: '12px 14px' }}>
            {renderItem(item)}
          </div>
        ))}
      </div>
    </div>
  );
}

function PriceTag({ price, cl = C }) {
  if (!price) return null;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
      background: 'transparent', border: `1px solid ${cl.border}`, color: cl.muted }}>
      {price}
    </span>
  );
}

const ctaBtn = {
  background: `linear-gradient(135deg,#006F87,#009EC2)`,
  border: 'none', borderRadius: 50, padding: '15px 36px',
  color: '#fff', fontSize: 15, fontWeight: 800, cursor: 'pointer',
  boxShadow: '0 8px 32px rgba(0,158,194,0.4)',
  display: 'inline-block',
};
